import React, { useState } from 'react';
import { ActionConfigPopup } from '../../../actions';
import * as Icons from 'lucide-react';

export const HeroSectionForm = ({ component, onUpdate }) => {
  const [showActionPopup, setShowActionPopup] = useState(false);
  const [editingButtonIndex, setEditingButtonIndex] = useState(null);

  const handleChange = (field, value) => {
    onUpdate(component.id, {
      specificAttrs: { ...component.specificAttrs, [field]: value }
    });
  };

  const { specificAttrs } = component;
  const {
    layout = 'center-aligned',
    heading = 'Welcome to Our Platform',
    subheading = 'Build amazing experiences with our powerful tools',
    description = '',
    showImage = true,
    imageUrl = '',
    imagePosition = 'right',
    backgroundType = 'solid',
    backgroundColor = '#1F2937',
    backgroundGradientStart = '#1F2937',
    backgroundGradientEnd = '#3B82F6',
    backgroundImageUrl = '',
    textColor = '#FFFFFF',
    buttons = [],
    height = 'medium',
    verticalAlignment = 'center'
  } = specificAttrs;

  // Add new button
  const handleAddButton = () => {
    const newButton = {
      id: `btn_${Date.now()}`,
      label: 'Get Started',
      variant: 'primary',
      size: 'medium',
      icon: '',
      action: null
    };
    handleChange('buttons', [...buttons, newButton]);
  };

  // Remove button
  const handleRemoveButton = (index) => {
    const updatedButtons = buttons.filter((_, i) => i !== index);
    handleChange('buttons', updatedButtons);
  };

  // Update button field
  const handleButtonChange = (index, field, value) => {
    const updatedButtons = [...buttons];
    updatedButtons[index] = { ...updatedButtons[index], [field]: value };
    handleChange('buttons', updatedButtons);
  };

  // Open action configuration popup
  const handleConfigureAction = (index) => {
    setEditingButtonIndex(index);
    setShowActionPopup(true);
  };

  // Save action to button
  const handleActionSelect = (actionConfig) => {
    if (editingButtonIndex !== null) {
      handleButtonChange(editingButtonIndex, 'action', actionConfig);
    }
    setShowActionPopup(false);
    setEditingButtonIndex(null);
  };

  // Available icons
  const availableIcons = [
    '', 'ArrowRight', 'ChevronRight', 'Play', 'Download', 'Send',
    'Star', 'Heart', 'ShoppingCart', 'User', 'Mail', 'Phone'
  ];

  return (
    <div style={{ display: 'grid', gap: '16px', padding: '4px' }}>
      {/* Section 1: Layout & Structure */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Layout & Structure
        </h4>

        {/* Layout Type */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            Layout Type
          </label>
          <select
            value={layout}
            onChange={(e) => handleChange('layout', e.target.value)}
            style={{
              width: '100%',
              padding: '6px 10px',
              fontSize: '13px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              backgroundColor: '#FFFFFF'
            }}
          >
            <option value="center-aligned">Center Aligned</option>
            <option value="left-aligned">Left Aligned</option>
            <option value="split-screen">Split Screen (Text + Image)</option>
            <option value="minimal">Minimal</option>
            <option value="full-width">Full Width</option>
          </select>
        </div>

        {/* Height */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            Height
          </label>
          <select
            value={height}
            onChange={(e) => handleChange('height', e.target.value)}
            style={{
              width: '100%',
              padding: '6px 10px',
              fontSize: '13px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              backgroundColor: '#FFFFFF'
            }}
          >
            <option value="small">Small (300px)</option>
            <option value="medium">Medium (500px)</option>
            <option value="large">Large (700px)</option>
            <option value="full-screen">Full Screen (100vh)</option>
          </select>
        </div>

        {/* Vertical Alignment */}
        <div>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            Vertical Alignment
          </label>
          <select
            value={verticalAlignment}
            onChange={(e) => handleChange('verticalAlignment', e.target.value)}
            style={{
              width: '100%',
              padding: '6px 10px',
              fontSize: '13px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              backgroundColor: '#FFFFFF'
            }}
          >
            <option value="top">Top</option>
            <option value="center">Center</option>
            <option value="bottom">Bottom</option>
          </select>
        </div>
      </div>

      {/* Section 2: Content */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Content
        </h4>

        {/* Heading */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            Main Heading *
          </label>
          <input
            type="text"
            value={heading}
            onChange={(e) => handleChange('heading', e.target.value)}
            placeholder="e.g., Welcome to Our Platform"
            style={{
              width: '100%',
              padding: '6px 10px',
              fontSize: '13px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px'
            }}
          />
        </div>

        {/* Subheading */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            Subheading
          </label>
          <input
            type="text"
            value={subheading}
            onChange={(e) => handleChange('subheading', e.target.value)}
            placeholder="e.g., Build amazing experiences"
            style={{
              width: '100%',
              padding: '6px 10px',
              fontSize: '13px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px'
            }}
          />
        </div>

        {/* Description */}
        <div>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            Description (optional)
          </label>
          <textarea
            value={description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Add a longer description here..."
            rows={3}
            style={{
              width: '100%',
              padding: '6px 10px',
              fontSize: '13px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              resize: 'vertical',
              fontFamily: 'inherit'
            }}
          />
        </div>
      </div>

      {/* Section 3: Hero Image */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Hero Image
        </h4>

        {/* Show Image Toggle */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            fontSize: '12px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={showImage}
              onChange={(e) => handleChange('showImage', e.target.checked)}
            />
            <span style={{ color: '#4B5563', fontWeight: '500' }}>Show Hero Image</span>
          </label>
        </div>

        {showImage && (
          <>
            {/* Image URL */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '12px', 
                fontWeight: '500', 
                marginBottom: '4px', 
                color: '#4B5563' 
              }}>
                Image URL
              </label>
              <input
                type="text"
                value={imageUrl}
                onChange={(e) => handleChange('imageUrl', e.target.value)}
                placeholder="https://example.com/hero-image.jpg"
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '13px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px'
                }}
              />
            </div>

            {/* Image Position */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '12px', 
                fontWeight: '500', 
                marginBottom: '4px', 
                color: '#4B5563' 
              }}>
                Image Position
              </label>
              <select
                value={imagePosition}
                onChange={(e) => handleChange('imagePosition', e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '13px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  backgroundColor: '#FFFFFF'
                }}
              >
                <option value="right">Right</option>
                <option value="left">Left</option>
                <option value="background">Background</option>
                <option value="top">Top</option>
              </select>
            </div>
          </>
        )}
      </div>

      {/* Section 4: Call-to-Action Buttons */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Call-to-Action Buttons
        </h4>

        {buttons.length === 0 && (
          <p style={{ 
            fontSize: '12px', 
            color: '#6B7280', 
            fontStyle: 'italic',
            marginBottom: '12px'
          }}>
            No buttons yet. Click "Add Button" to add call-to-action buttons.
          </p>
        )}

        {buttons.map((button, index) => (
          <div 
            key={button.id}
            style={{ 
              padding: '12px',
              backgroundColor: '#FFFFFF',
              borderRadius: '6px',
              border: '1px solid #E5E7EB',
              marginBottom: '8px'
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <span style={{ 
                fontSize: '12px', 
                fontWeight: '600', 
                color: '#374151' 
              }}>
                Button {index + 1}
              </span>
              <button
                onClick={() => handleRemoveButton(index)}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  color: '#DC2626',
                  backgroundColor: '#FEE2E2',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Remove ✕
              </button>
            </div>

            {/* Button Label */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                marginBottom: '3px', 
                color: '#6B7280' 
              }}>
                Button Label *
              </label>
              <input
                type="text"
                value={button.label}
                onChange={(e) => handleButtonChange(index, 'label', e.target.value)}
                placeholder="e.g., Get Started, Learn More"
                style={{
                  width: '100%',
                  padding: '5px 8px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px'
                }}
              />
            </div>

            {/* Button Variant */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                marginBottom: '3px', 
                color: '#6B7280' 
              }}>
                Style
              </label>
              <select
                value={button.variant}
                onChange={(e) => handleButtonChange(index, 'variant', e.target.value)}
                style={{
                  width: '100%',
                  padding: '5px 8px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  backgroundColor: '#FFFFFF'
                }}
              >
                <option value="primary">Primary (Filled)</option>
                <option value="secondary">Secondary (Outlined)</option>
                <option value="ghost">Ghost (Text Only)</option>
              </select>
            </div>

            {/* Button Size */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                marginBottom: '3px', 
                color: '#6B7280' 
              }}>
                Size
              </label>
              <select
                value={button.size}
                onChange={(e) => handleButtonChange(index, 'size', e.target.value)}
                style={{
                  width: '100%',
                  padding: '5px 8px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  backgroundColor: '#FFFFFF'
                }}
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>

            {/* Button Icon */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                marginBottom: '3px', 
                color: '#6B7280' 
              }}>
                Icon (optional)
              </label>
              <select
                value={button.icon || ''}
                onChange={(e) => handleButtonChange(index, 'icon', e.target.value)}
                style={{
                  width: '100%',
                  padding: '5px 8px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  backgroundColor: '#FFFFFF'
                }}
              >
                {availableIcons.map(iconName => (
                  <option key={iconName || 'none'} value={iconName}>
                    {iconName || 'No Icon'}
                  </option>
                ))}
              </select>
            </div>

            {/* Action */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                marginBottom: '3px', 
                color: '#6B7280' 
              }}>
                Action
              </label>
              <button
                onClick={() => handleConfigureAction(index)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '12px',
                  color: '#FFFFFF',
                  backgroundColor: button.action ? '#10B981' : '#3B82F6',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {button.action ? '✓ Action Configured' : 'Configure Action'}
              </button>
              {button.action && (
                <p style={{ 
                  fontSize: '10px', 
                  color: '#6B7280', 
                  marginTop: '4px',
                  fontStyle: 'italic'
                }}>
                  {button.action.actionType || 'Action set'}
                </p>
              )}
            </div>
          </div>
        ))}

        {/* Add Button */}
        <button
          onClick={handleAddButton}
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: '13px',
            color: '#3B82F6',
            backgroundColor: '#EFF6FF',
            border: '1px dashed #3B82F6',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          + Add Button
        </button>
      </div>

      {/* Section 5: Background & Colors */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Background & Colors
        </h4>

        {/* Background Type */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            Background Type
          </label>
          <select
            value={backgroundType}
            onChange={(e) => handleChange('backgroundType', e.target.value)}
            style={{
              width: '100%',
              padding: '6px 10px',
              fontSize: '13px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              backgroundColor: '#FFFFFF'
            }}
          >
            <option value="solid">Solid Color</option>
            <option value="gradient">Gradient</option>
            <option value="image">Image</option>
          </select>
        </div>

        {/* Solid Background Color */}
        {backgroundType === 'solid' && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              marginBottom: '4px', 
              color: '#4B5563' 
            }}>
              Background Color
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={backgroundColor}
                onChange={(e) => handleChange('backgroundColor', e.target.value)}
                style={{
                  width: '50px',
                  height: '34px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={backgroundColor}
                onChange={(e) => handleChange('backgroundColor', e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px'
                }}
              />
            </div>
          </div>
        )}

        {/* Gradient Colors */}
        {backgroundType === 'gradient' && (
          <>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '12px', 
                fontWeight: '500', 
                marginBottom: '4px', 
                color: '#4B5563' 
              }}>
                Gradient Start Color
              </label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={backgroundGradientStart}
                  onChange={(e) => handleChange('backgroundGradientStart', e.target.value)}
                  style={{
                    width: '50px',
                    height: '34px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                />
                <input
                  type="text"
                  value={backgroundGradientStart}
                  onChange={(e) => handleChange('backgroundGradientStart', e.target.value)}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    fontSize: '12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px'
                  }}
                />
              </div>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '12px', 
                fontWeight: '500', 
                marginBottom: '4px', 
                color: '#4B5563' 
              }}>
                Gradient End Color
              </label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={backgroundGradientEnd}
                  onChange={(e) => handleChange('backgroundGradientEnd', e.target.value)}
                  style={{
                    width: '50px',
                    height: '34px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                />
                <input
                  type="text"
                  value={backgroundGradientEnd}
                  onChange={(e) => handleChange('backgroundGradientEnd', e.target.value)}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    fontSize: '12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px'
                  }}
                />
              </div>
            </div>
          </>
        )}

        {/* Background Image URL */}
        {backgroundType === 'image' && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              marginBottom: '4px', 
              color: '#4B5563' 
            }}>
              Background Image URL
            </label>
            <input
              type="text"
              value={backgroundImageUrl}
              onChange={(e) => handleChange('backgroundImageUrl', e.target.value)}
              placeholder="https://example.com/background.jpg"
              style={{
                width: '100%',
                padding: '6px 10px',
                fontSize: '13px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px'
              }}
            />
          </div>
        )}

        {/* Text Color */}
        <div>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            Text Color
          </label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="color"
              value={textColor}
              onChange={(e) => handleChange('textColor', e.target.value)}
              style={{
                width: '50px',
                height: '34px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            />
            <input
              type="text"
              value={textColor}
              onChange={(e) => handleChange('textColor', e.target.value)}
              style={{
                flex: 1,
                padding: '6px 10px',
                fontSize: '12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px'
              }}
            />
          </div>
        </div>
      </div>

      {/* Action Configuration Popup */}
      {showActionPopup && (
        <ActionConfigPopup
          isOpen={showActionPopup}
          onClose={() => {
            setShowActionPopup(false);
            setEditingButtonIndex(null);
          }}
          onActionSelect={handleActionSelect}
        />
      )}
    </div>
  );
};